/**
 * O seletor de item: digita, vê, escolhe.
 *
 * Substitui o `<datalist>` do sistema antigo, que era texto puro e obrigava a
 * conhecer o nome exato. Aqui a busca é a MESMA da vitrine (sem acento,
 * pedaços de palavra, código exato) e cada sugestão mostra a FOTO — a
 * miniatura vem embutida no JSON da resposta, então as sugestões aparecem
 * completas de uma vez, sem nenhuma requisição de imagem.
 *
 * Rápido de propósito: espera 250 ms de silêncio antes de buscar (digitar
 * "cimento" não são sete buscas), descarta resposta atrasada de busca antiga,
 * e anda por teclado (↓ ↑ Enter Esc).
 */

import { api, type BuscaDeItens, type ItemDaLista } from '../api.js';
import { escapar, numero } from './formato.js';

export type ItemEscolhido = Pick<
  ItemDaLista,
  'codigo' | 'nome' | 'unidade' | 'saldo' | 'miniatura' | 'foto'
>;

export function montarBuscaItem(
  caixa: HTMLElement,
  aoEscolher: (item: ItemEscolhido) => void,
  placeholder = 'Busque por código ou nome…',
): { focar(): void; limpar(): void } {
  caixa.classList.add('busca-item');
  caixa.innerHTML = `
    <input type="search" class="bi-entrada" placeholder="${escapar(placeholder)}"
           autocomplete="off" aria-label="Buscar item">
    <div class="bi-lista" hidden></div>`;

  const entrada = caixa.querySelector<HTMLInputElement>('.bi-entrada')!;
  const lista = caixa.querySelector<HTMLElement>('.bi-lista')!;

  let sugestoes: ItemEscolhido[] = [];
  let ativa = -1;
  let temporizador: number | undefined;
  let ultimaBusca = 0;

  function fechar(): void {
    lista.hidden = true;
    ativa = -1;
  }

  function escolher(i: number): void {
    const item = sugestoes[i];
    if (!item) return;
    entrada.value = '';
    fechar();
    aoEscolher(item);
  }

  function desenhar(): void {
    if (sugestoes.length === 0) {
      lista.innerHTML = `<div class="bi-nada">Nada encontrado com esse texto.</div>`;
      lista.hidden = false;
      return;
    }
    lista.innerHTML = sugestoes
      .map(
        (s, i) => `
      <button class="bi-opcao ${i === ativa ? 'ativa' : ''}" data-i="${i}" type="button">
        <span class="bi-foto">${
          s.miniatura ? `<img src="${s.miniatura}" alt="">` : '<span class="sem">—</span>'
        }</span>
        <span class="bi-nome"><b>${escapar(s.nome)}</b>
          <span class="sub-inline">${escapar(s.codigo)} · saldo ${numero(s.saldo)} ${escapar(s.unidade ?? '')}</span>
        </span>
      </button>`,
      )
      .join('');
    lista.hidden = false;
  }

  async function buscar(): Promise<void> {
    const termo = entrada.value.trim();
    if (termo.length < 2) {
      fechar();
      return;
    }
    const esta = ++ultimaBusca;
    try {
      const r = await api.get<BuscaDeItens>(
        `/api/itens?busca=${encodeURIComponent(termo)}&porPagina=8`,
      );
      if (esta !== ultimaBusca) return; // já digitou mais — esta resposta é velha
      sugestoes = r.itens;
      ativa = -1;
      desenhar();
    } catch {
      // Busca que falhou não merece alerta — a pessoa digita de novo e passa.
      fechar();
    }
  }

  entrada.addEventListener('input', () => {
    window.clearTimeout(temporizador);
    temporizador = window.setTimeout(() => void buscar(), 250);
  });

  entrada.addEventListener('keydown', (e) => {
    if (lista.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const passo = e.key === 'ArrowDown' ? 1 : -1;
      ativa = (ativa + passo + sugestoes.length) % Math.max(sugestoes.length, 1);
      desenhar();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      escolher(ativa >= 0 ? ativa : 0);
    } else if (e.key === 'Escape') {
      fechar();
    }
  });

  lista.addEventListener('mousedown', (e) => {
    // mousedown, não click: o blur do input fecharia a lista antes do click.
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-i]');
    if (!b) return;
    e.preventDefault();
    escolher(Number(b.dataset['i']));
  });

  entrada.addEventListener('blur', () => {
    // Um respiro antes de fechar, para o mousedown da lista chegar primeiro.
    window.setTimeout(fechar, 150);
  });

  return {
    focar: () => entrada.focus(),
    limpar: () => {
      entrada.value = '';
      fechar();
    },
  };
}
